/* The one DOM helper the pages share. Text always goes in as text: nothing here, and nothing
   built on it, ever assigns innerHTML, which is what keeps user-written names and notes from
   being read as markup. */

type Child = Node | string | null | undefined | false;

type Props<K extends keyof HTMLElementTagNameMap> = Partial<
  Omit<HTMLElementTagNameMap[K], "style" | "dataset" | "children">
> & {
  /** Shorthand for className. */
  class?: string;
  style?: Partial<CSSStyleDeclaration>;
  dataset?: Record<string, string>;
  /** Attributes that have no property, such as aria-*. */
  attrs?: Record<string, string>;
};

const kept = (children: (Child | Child[])[]): (Node | string)[] =>
  children.flat().filter((c): c is Node | string => c != null && c !== false);

/** `el("button", {class: "btn", onclick}, "Label")` */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Props<K> = {},
  ...children: (Child | Child[])[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  const { class: cls, style, dataset, attrs, ...rest } = props;
  if (cls) node.className = cls;
  if (style) Object.assign(node.style, style);
  if (dataset) Object.assign(node.dataset, dataset);
  if (attrs) for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  Object.assign(node, rest);
  node.append(...kept(children));
  return node;
}

/** The element with this id. A page that is missing one of its own ids is a bug, so throw. */
export function byId<T extends HTMLElement = HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`#${id} is not on the page`);
  return node as T;
}

/** Empty a node, then fill it. */
export function fill(node: Element, ...children: (Child | Child[])[]): void {
  node.replaceChildren(...kept(children));
}
